import React, { useEffect, useState, useRef } from "react";
import { loadUser, saveUser, uploadProfileImage } from "../utils/Network";
import SelectTalent from "../components/SelectTalent";
import SelectGender from "../components/SelectGender";
import SelectYear from "../components/SelectYear";
import SelectMonth from "../components/SelectMonth";
import SelectDay from "../components/SelectDay";
import SelectCities from "../components/SelectCities";
import SelectCountries from "../components/SelectCountries";
import { userAvatar } from "../utils";

function formatDateTime(value) {
  if (!value || value === "null" || value === "None") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function ToggleRow({ checked, onChange, title, hint }) {
  return (
    <label className="cms-user-profile-toggle">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="cms-user-profile-toggle__copy">
        <span className="cms-user-profile-toggle__title">{title}</span>
        {hint ? <span className="cms-user-profile-toggle__hint">{hint}</span> : null}
      </span>
    </label>
  );
}

export default function UserContent({ id }) {
  const profile_image_ref = useRef(null);

  const [user, setUser] = useState(null);
  const [year, setBirthYear] = useState(null);
  const [month, setBirthMonth] = useState(null);
  const [day, setBirthDay] = useState(null);
  const [birth_country, setBirthCountry] = useState(null);
  const [birth_city, setBirthCity] = useState(null);
  const [living_country, setLivingCountry] = useState(null);
  const [living_city, setLivingCity] = useState(null);
  const [gender, setGender] = useState(null);
  const [selected_talent, setTalent] = useState(null);

  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(!!id);

  const patchUser = (fields) => {
    setUser((prev) => ({ ...(prev || {}), ...fields }));
  };

  useEffect(() => {
    if (!id) {
      setUser({
        first_name: "",
        last_name: "",
        email: "",
        is_active: true,
        is_verified: true,
        is_test_user: false,
      });
      setPageLoading(false);
      return;
    }

    setPageLoading(true);
    loadUser(id).then((profile) => {
      setUser(profile || {});
      if (profile) {
        setGender(profile.gender);
        if (profile.birth_date) {
          const birthdate = profile.birth_date.split("-");
          setBirthYear(birthdate[0]);
          setBirthMonth(birthdate[1]);
          setBirthDay(birthdate[2]);
        }
        if (profile.country_id) setBirthCountry(profile.country_id);
        if (profile.city_id) setBirthCity(profile.city_id);
        if (profile.livingcountry_id) setLivingCountry(profile.livingcountry_id);
        if (profile.livingcity_id) setLivingCity(profile.livingcity_id);
        else if (profile.livingcity?.id) setLivingCity(profile.livingcity.id);
        if (profile.gender > -1) setGender(profile.gender);
      }
      setPageLoading(false);
    });
  }, [id]);

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !user) return;
    setLoading(true);
    uploadProfileImage(user, file).then((response) => {
      if (response && response.id) setUser(response);
      setLoading(false);
    });
  };

  const onChange = (e) => {
    patchUser({ [e.target.name]: e.target.value });
  };

  const onValue = (value, name) => {
    switch (name) {
      case "talent":
        patchUser({ talent: value });
        setTalent(value);
        break;
      case "gender":
        patchUser({ gender: value });
        setGender(value);
        break;
      case "country":
        patchUser({ country_id: value });
        setBirthCountry(value);
        break;
      case "city":
        patchUser({ city_id: value });
        setBirthCity(value);
        break;
      case "living_country":
        patchUser({ livingcountry_id: value });
        setLivingCountry(value);
        break;
      case "living_city":
        patchUser({ livingcity_id: value });
        setLivingCity(value);
        break;
      case "year": {
        setBirthYear(value);
        const date = (user?.birth_date || "2021-01-01").split("-");
        date[0] = value;
        patchUser({ birth_date: `${date[0]}-${date[1]}-${date[2]}` });
        break;
      }
      case "month": {
        setBirthMonth(value);
        const date = (user?.birth_date || "2021-01-01").split("-");
        date[1] = value;
        patchUser({ birth_date: `${date[0]}-${date[1]}-${date[2]}` });
        break;
      }
      case "day": {
        setBirthDay(value);
        const date = (user?.birth_date || "2021-01-01").split("-");
        date[2] = value;
        patchUser({ birth_date: `${date[0]}-${date[1]}-${date[2]}` });
        break;
      }
      default:
        break;
    }
  };

  const saveChanges = () => {
    setError(null);
    setDone(null);
    setLoading(true);

    if (!user || !user.first_name || user.first_name.length < 3) {
      setError("Please Enter First Name");
      setLoading(false);
      return;
    }
    if (!user.last_name || user.last_name.length < 3) {
      setError("Please Enter Last Name");
      setLoading(false);
      return;
    }
    if (user.gender == null) {
      setError("Please Select Gender");
      setLoading(false);
      return;
    }
    if (!user.email) {
      setError("Please Enter Email");
      setLoading(false);
      return;
    }

    const payload = { ...user };
    if (id) payload.id = id;

    saveUser(payload).then((data) => {
      if (data?.errors) {
        if (data.errors.email) setError("User with this email already exists");
        if (data.errors.password) setError("Password is too short");
      } else {
        setDone("User information saved");
      }
      setLoading(false);
    });
  };

  const displayName = user
    ? `${(user.first_name || "").trim()} ${(user.last_name || "").trim()}`.trim()
    : "";

  return (
    <div className="content-wrapper cms-user-profile-page">
      <section className="content-header">
        <h1>Profile information</h1>
        <ol className="breadcrumb">
          <li>
            <a href="/cms">
              <i className="fa fa-dashboard" /> Home
            </a>
          </li>
          <li>
            <a href="/cms/users">Users</a>
          </li>
          <li className="active">{id ? "Edit" : "New"}</li>
        </ol>
      </section>

      <section className="content">
        {pageLoading || !user ? (
          <div className="cms-user-profile-status">Loading profile…</div>
        ) : (
          <div className="row">
            <div className="col-md-4">
              <div className="cms-user-profile-card cms-user-profile-card--aside">
                <div className="cms-user-profile-aside__hero">
                  <button
                    type="button"
                    className="cms-user-profile-avatar"
                    onClick={() => profile_image_ref.current && profile_image_ref.current.click()}
                    title="Change photo"
                  >
                    <img src={userAvatar(user)} alt="" />
                    <span className="cms-user-profile-avatar__hint">
                      <i className="fas fa-camera" aria-hidden />
                    </span>
                  </button>
                  <input
                    ref={profile_image_ref}
                    type="file"
                    className="hide"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <h2 className="cms-user-profile-aside__name">{displayName || "New user"}</h2>
                  <p className="cms-user-profile-aside__email mb-0">{user.email || "—"}</p>
                  {user.is_test_user ? (
                    <span className="cms-user-profile-badge">Test user</span>
                  ) : null}
                </div>
                <dl className="cms-user-profile-meta">
                  <div>
                    <dt>Last login</dt>
                    <dd>{formatDateTime(user.last_login)}</dd>
                  </div>
                  <div>
                    <dt>Last online</dt>
                    <dd>{formatDateTime(user.last_online)}</dd>
                  </div>
                  <div>
                    <dt>Role</dt>
                    <dd>{user.role || "participant"}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="col-md-8">
              <div className="cms-user-profile-card">
                <h3 className="cms-user-profile-card__title">Account</h3>
                <div className="row">
                  <div className="col-sm-6 cms-user-profile-field">
                    <label>First name</label>
                    <input
                      type="text"
                      name="first_name"
                      value={user.first_name || ""}
                      onChange={onChange}
                      className="form-control"
                      placeholder="First name"
                    />
                  </div>
                  <div className="col-sm-6 cms-user-profile-field">
                    <label>Last name</label>
                    <input
                      type="text"
                      name="last_name"
                      value={user.last_name || ""}
                      onChange={onChange}
                      className="form-control"
                      placeholder="Last name"
                    />
                  </div>
                  <div className="col-sm-6 cms-user-profile-field">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={user.email || ""}
                      onChange={onChange}
                      className="form-control"
                      placeholder="Email"
                    />
                  </div>
                  <div className="col-sm-6 cms-user-profile-field">
                    <label>Password</label>
                    <input
                      type="password"
                      name="password"
                      value={user.password || ""}
                      onChange={onChange}
                      className="form-control"
                      placeholder={id ? "Leave blank to keep" : "Password"}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              </div>

              <div className="cms-user-profile-card">
                <h3 className="cms-user-profile-card__title">Personal</h3>
                <div className="row">
                  <div className="col-sm-6 cms-user-profile-field">
                    <label>Gender</label>
                    <SelectGender width="100%" value={gender} border="1" callback={onValue} />
                  </div>
                  <div className="col-sm-6 cms-user-profile-field">
                    <label>Talent</label>
                    <SelectTalent value={selected_talent} border="1" callback={onValue} />
                  </div>
                  <div className="col-sm-6 cms-user-profile-field">
                    <label>Phone</label>
                    <input
                      className="form-control"
                      type="tel"
                      name="phone_number"
                      onChange={onChange}
                      value={user.phone_number || ""}
                      placeholder="Phone number"
                    />
                  </div>
                  <div className="col-sm-6 cms-user-profile-field">
                    <label>Date of birth</label>
                    <div className="cms-user-profile-date">
                      <SelectYear value={year} name="year" width="100%" border="1" callback={onValue} />
                      <SelectMonth value={month} name="month" width="100%" border="1" callback={onValue} />
                      <SelectDay value={day} name="day" width="100%" border="1" callback={onValue} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="cms-user-profile-card">
                <h3 className="cms-user-profile-card__title">Location</h3>
                <div className="row">
                  <div className="col-sm-6 cms-user-profile-field">
                    <label>Born in</label>
                    <div className="cms-user-profile-pair">
                      <SelectCountries
                        kurds={1}
                        value={birth_country}
                        name="country"
                        width="100%"
                        border="1"
                        callback={onValue}
                      />
                      <SelectCities
                        country={birth_country}
                        key={`birth-${birth_country}`}
                        value={birth_city}
                        name="city"
                        width="100%"
                        border="1"
                        callback={onValue}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6 cms-user-profile-field">
                    <label>Living in</label>
                    <div className="cms-user-profile-pair">
                      <SelectCountries
                        value={living_country}
                        name="living_country"
                        width="100%"
                        border="1"
                        callback={onValue}
                      />
                      <SelectCities
                        country={living_country}
                        key={`live-${living_country}`}
                        value={living_city}
                        name="living_city"
                        width="100%"
                        border="1"
                        callback={onValue}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="cms-user-profile-card">
                <h3 className="cms-user-profile-card__title">Social</h3>
                <div className="row">
                  {[
                    ["website", "Website"],
                    ["facebook", "Facebook"],
                    ["instagram", "Instagram"],
                    ["youtube", "YouTube"],
                    ["snapchat", "Snapchat"],
                    ["spotify", "Spotify"],
                  ].map(([name, label]) => (
                    <div key={name} className="col-sm-6 cms-user-profile-field">
                      <label>{label}</label>
                      <input
                        type="text"
                        name={name}
                        value={user[name] || ""}
                        onChange={onChange}
                        className="form-control"
                        placeholder={label}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="cms-user-profile-card">
                <h3 className="cms-user-profile-card__title">Access</h3>
                <div className="cms-user-profile-toggles">
                  <ToggleRow
                    checked={user.is_active !== false}
                    onChange={(v) => patchUser({ is_active: v })}
                    title="Active"
                    hint="Inactive accounts cannot sign in"
                  />
                  <ToggleRow
                    checked={!!user.is_verified}
                    onChange={(v) => patchUser({ is_verified: v })}
                    title="Verified"
                    hint="Email / account verification flag"
                  />
                  <ToggleRow
                    checked={!!user.is_test_user}
                    onChange={(v) => patchUser({ is_test_user: v })}
                    title="Test user"
                    hint="Can see and join staff-preview contests, same as admin, editor, and referee"
                  />
                </div>
              </div>

              {error ? <div className="alert alert-danger">{error}</div> : null}
              {done ? <div className="alert alert-success">{done}</div> : null}

              <div className="cms-user-profile-actions">
                <a href="/cms/users" className="btn btn-default">
                  Cancel
                </a>
                <button
                  type="button"
                  onClick={saveChanges}
                  disabled={loading}
                  className="btn btn-info"
                >
                  {loading ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
